import styled from "styled-components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CategoryFilter } from "../category-filter";
import { Gender, CaseName, Num } from "../../grammar/types";
import {
    CASE_ORDER,
    NUM_ORDER,
    DeclensionResult,
    DerivedForm,
    LexicalHints,
    decline,
    parseMwHints,
} from "../../grammar/declension";
import { Prakriya, FormButton, Variant } from "./prakriya";

// Monier-Williams wraps its grammatical labels in <lex>, sometimes typed —
// phw, hw, hwifc, hwalt, hwinfo, nhw, part. The previous pattern matched
// type="nwh", a transposition that occurs nowhere in the data (MW has 675
// nhw and no nwh), so every typed <lex> was skipped and its gender lost.
const lexPattern = new RegExp('(?:(?:<lex(?:\\s+type="[^"]*")?>)|(?:<div n="1">))(.+?)</(?:lex|div)>', "gm");

const genderList: Array<{ name: Gender; title: string }> = [
    { name: "mas", title: "Masculine" },
    { name: "fem", title: "Feminine" },
    { name: "neu", title: "Neuter" },
];

/** One cell of the table; clicking a form reveals its derivation. */
const Cell = (props: {
    forms: DerivedForm[];
    onSelect: (f: DerivedForm) => void;
    selected?: DerivedForm;
}) => {
    if (props.forms.length === 0) return <td>&mdash;</td>;

    return (
        <td>
            {props.forms.map((f, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <Variant> / </Variant>}
                    <FormButton
                        type="button"
                        data-selected={props.selected === f}
                        onClick={() => props.onSelect(f)}
                        title="Show derivation"
                    >
                        {f.text}
                    </FormButton>
                </React.Fragment>
            ))}
        </td>
    );
};

const DeclensionTable = (props: {
    result: DeclensionResult;
    onSelect: (f: DerivedForm) => void;
    selected?: DerivedForm;
}) => {
    const { t } = useTranslation();

    return (
        <StyledDeclensionTable>
            <tbody>
                <tr>
                    <THead />
                    <THead>{t("singular")}</THead>
                    <THead>{t("dual")}</THead>
                    <THead>{t("plural")}</THead>
                </tr>
                {CASE_ORDER.map((caseName: CaseName) => (
                    <tr key={caseName}>
                        <THead className="case">{caseName}.</THead>
                        {NUM_ORDER.map((num: Num) => (
                            <Cell
                                key={num}
                                forms={props.result.cells[caseName][num]}
                                onSelect={props.onSelect}
                                selected={props.selected}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </StyledDeclensionTable>
    );
};

export const DeclensionWindow = React.forwardRef(
    (
        props: {
            word: string;
            gender: Gender;
            availableGenders: Gender[];
            hints?: LexicalHints;
            /** Set when the same headword is also a verbal root. */
            onConjugate?: () => void;
            onClose: () => void;
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const shown: Gender[] = props.availableGenders?.length
            ? props.availableGenders
            : ["mas", "fem", "neu"];
        const initial = shown.includes(props.gender) ? props.gender : shown[0];

        const [gender, setGender] = useState<Gender>(initial);
        const [result, setResult] = useState<DeclensionResult | null>(null);
        const [selected, setSelected] = useState<DerivedForm | undefined>(undefined);
        const [failed, setFailed] = useState(false);

        // The grammar engine is a WebAssembly module loaded on demand, so the
        // table arrives asynchronously the first time the window is opened.
        useEffect(() => {
            let cancelled = false;
            setResult(null);
            setSelected(undefined);
            setFailed(false);

            decline(props.word, gender, props.hints ?? {})
                .then(r => { if (!cancelled) setResult(r); })
                .catch(() => { if (!cancelled) setFailed(true); });

            return () => { cancelled = true; };
        }, [props.word, gender, props.hints]);

        return (
            <StyledDeclension ref={ref as React.Ref<HTMLDivElement>}>
                <CloseButton onClick={props.onClose} className="icon-cancel" />
                <Word>{props.word}</Word>
                <GenderSelector
                    name="gender"
                    categoryList={genderList.filter(g => shown.includes(g.name))}
                    defaultState={gender}
                    onChange={(v: string) => setGender(v as Gender)}
                />

                {failed && t("no-declension")}
                {!failed && !result && <Loading>{t("loading")}</Loading>}
                {!failed && result && (
                    <>
                        <DeclensionTable result={result} onSelect={setSelected} selected={selected} />
                        {result.note && <Analysis>{result.note}</Analysis>}
                        {props.onConjugate &&
                            <OtherParadigm type="button" onClick={props.onConjugate}>
                                &radic;{props.word} &mdash; conjugate
                            </OtherParadigm>}
                        {selected && <Prakriya form={selected} onClose={() => setSelected(undefined)} />}
                    </>
                )}
            </StyledDeclension>
        );
    },
);

/**
 * Marks up the gender abbreviations in a dictionary entry as links, and reports
 * which genders and grammatical hints the entry records.
 */
export const parseGenders = (
    word: string,
    content: string,
): { genders: Gender[]; content: string; hints: LexicalHints } => {
    const lexGenderPattern = new RegExp('(?<=(?:[^\\w]|^)m?f?)([mfn]\\.?)(?=f?n?(?:[^\\w\\=]|$))', "gm");
    const genders: Gender[] = [];

    content = content.replaceAll(lexPattern, (_, matchGroup: string) => {
        return `<lex>${matchGroup.replaceAll(lexGenderPattern, (__, genMatch) => {
            let genderName: Gender | undefined;
            switch (genMatch[0]) {
                case "m": genderName = "mas"; break;
                case "f": genderName = "fem"; break;
                case "n": genderName = "neu"; break;
            }
            if (genderName && !genders.includes(genderName)) genders.push(genderName);
            return `<a class="gender" data-word="${word}" href="#${genMatch[0]}">${genMatch}</a>`;
        })}</lex>`;
    });

    // Two sources for the gender, and they are not equally good. Measured over
    // all 287,634 MW entries, the two agree 95.2% of the time; where they differ
    // the structured `<info lex="..."/>` attribute finds genders the free text
    // misses in 4.0% of cases (mostly `m:f:n` adjectives written as one cluster),
    // and the free text claims genders the structured markup does not in 0.9%
    // (stray "m." and "n." picked up out of the surrounding prose). Outright
    // conflicts number 17 in the whole dictionary.
    //
    // So the structured attribute wins where it exists, and the free-text scan
    // fills the gaps rather than being unioned in — a spurious gender is not a
    // harmless extra tab, it renders a whole paradigm the word does not have.
    //
    // `<info lex>` also records *how* the feminine is formed (`f#A` for ṭāp,
    // `f#I` for ṅīp), which the free text cannot express and which is what tells
    // the engine that sītā is a nyāp derivative rather than a stem in -ā.
    const hints = parseMwHints(content);
    const structured = hints.genders ?? [];

    return {
        genders: structured.length > 0 ? structured : genders,
        content,
        hints,
    };
};

const StyledDeclension = styled.div`
  position: fixed;
  text-align: center;
  z-index: 1000;
  top: 50%;
  left: 50%;
  min-width: 300px;
  max-width: min(560px, 94vw);
  max-height: 90vh;
  overflow-y: auto;
  min-height: 300px;
  transform: translate(-50%, -50%);
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.gray};
  padding: 5px;
  box-shadow: 0px 2px 2px rgb(0 0 0 / 30%);
`;

const Word = styled.div`
    text-align: center;
    font-weight: bold;
    margin-top: 15px
`;

const Loading = styled.div`
    margin: 40px 0;
    opacity: 0.6;
`;

/* A word can be both a noun and a root; this reaches the other paradigm. */
const OtherParadigm = styled.button`
    font: inherit;
    font-size: 0.7em;
    background: none;
    border: none;
    cursor: pointer;
    opacity: 0.75;
    margin-bottom: 12px;
    text-decoration: underline dotted;
    &:hover { opacity: 1; }
`;

const Analysis = styled.div`
    font-size: 0.7em;
    opacity: 0.75;
    margin: -15px 0 10px 0;
`;









const StyledDeclensionTable = styled.table`
  font-size: 0.7em;
  margin: 10px auto 25px auto;
  width: 100%;

  td {
      padding: 3px;
      white-space: nowrap;
  }
`;

const THead = styled.td`
    font-weight: bold;
    text-transform: capitalize;
`;

const CloseButton = styled.a`
  position: absolute;
  right: 20px;
  top: 13px;
  display: block;
  font-weight: bold;
  cursor: pointer;
`;

const GenderSelector = styled(CategoryFilter)`
  margin-top: 15px;
  justify-content: center;
`;
